import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { Link } from 'react-router';
import { ROUTE } from '@/routes/constants';
import { requestCaptcha, sendResetPassword } from '@/services/generatedAPIs';
import { generateVerificationToken } from '@/lib/crypto';
import { toast } from 'sonner';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAutoHideScroll } from '@/hooks/use-auto-hide-scroll';
import { EmailIcon } from './login-icons';
import { ThemeColors } from './login-theme';

// 手机横屏(844×390)「申請重設密碼」右侧表单列：标题固定不滚动,其余内容(说明/输入/按钮/登入)
// 装进可局部滚动的表单体(overflow-y:auto)。逻辑与桌面/竖屏版完全一致(onAction 不变)。

async function onAction(formData: FormData, t: TFunction): Promise<void> {
  const email = formData.get('email');
  if (!email || typeof email !== 'string') return;
  try {
    const rsp1 = await requestCaptcha({ nonce: email });
    if (rsp1.code === 200 && !rsp1.data) {
      const rsp2 = await sendResetPassword({ t: generateVerificationToken(email) });
      if (rsp2.code === 200) toast(t('component.sendsuccess'));
      else toast(rsp2.message);
    } else {
      toast(rsp1.message);
    }
  } catch {
    toast(t('component.networkError') ?? 'Network error, please try again.');
  }
}

interface Props {
  colors: ThemeColors;
}

export default function MobileLandscapeResetForm({ colors }: Props) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useAutoHideScroll<HTMLDivElement>();

  return (
    <form
      action={async (fd: FormData) => { setIsLoading(true); await onAction(fd, t); setIsLoading(false); }}
      style={{ position: 'absolute', left: 524, top: 60, width: 203, height: 285, display: 'flex', flexDirection: 'column' }}
    >
      {/* 标题(固定,不随表单体滚动) */}
      <h2 style={{ flex: '0 0 auto', margin: 0, textAlign: 'center', fontSize: 20, fontWeight: 600, color: '#000', lineHeight: '24px', letterSpacing: '-0.01em' }}>
        {t('user.reset-password-title')}
      </h2>

      {/* 表单体(内容超长时局部滚动;细滚动条仅滚动时显现) */}
      <div ref={scrollRef} className="ml-thin-scroll" style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', marginTop: 12, paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, textAlign: 'center', fontSize: 11, color: '#575C60', lineHeight: '16px' }}>
          {t('user.reset-password-subtitle')}
        </p>

        <div>
          <label htmlFor="ml-reset-email" style={{ display: 'block', fontSize: 12, color: '#575C60', lineHeight: '12px', marginBottom: 8 }}>
            {t('user.email')}
          </label>
          <div style={{ position: 'relative', height: 29 }}>
            <EmailIcon aria-hidden="true" style={{ position: 'absolute', left: 12, top: 8, width: 13, height: 13, color: '#575C60', zIndex: 1 }} />
            <Input
              id="ml-reset-email" name="email" type="email" required placeholder="scholar@atelier.edu.hk"
              onInvalid={(e: any) => { e.target.setCustomValidity(t('user.email-error')); }}
              onInput={(e: any) => { e.target.setCustomValidity(''); }}
              className="pl-[30px] pr-3 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
              style={{ width: '100%', height: 29, borderRadius: 14.5, background: colors.inputBg, border: 0, fontSize: 11, color: '#1F1F1F', ['--tw-ring-color' as any]: colors.link + '66' }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%', height: 33, borderRadius: 16.5, background: colors.buttonBg, color: colors.buttonText,
            border: 0, fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: isLoading ? 0.6 : 1,
          }}
          className="hover:brightness-[0.97]"
        >
          {isLoading ? '...' : t('user.send-reset-password')}
        </button>

        <div style={{ textAlign: 'center', fontSize: 12 }}>
          <Link to={ROUTE.LOGIN} style={{ color: colors.link }} className="underline underline-offset-2">{t('user.sign-in')}</Link>
        </div>
      </div>
    </form>
  );
}
